/**
 * Session, current user and installed modules — shared server state via TanStack Query.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import {
  adminMeRetrieve,
  adminModulesList,
  auth,
  storeMeRetrieve,
  storeModulesList,
  vendorMeRetrieve,
  vendorModulesList,
  type AdminMe,
  type ApiError,
  type AuthState,
  type InstalledModule,
  type StoreMe,
  type VendorMe,
} from "@motolynk/api-client";

import type { ModuleDefinition, Panel } from "./module";

export const SESSION_KEY = ["auth", "session"] as const;
export const meKey = (panel: Panel) => ["me", panel] as const;
export const modulesKey = (panel: Panel) => ["modules", panel] as const;

export type PanelMe = AdminMe | VendorMe | StoreMe;

const meFetchers = { admin: adminMeRetrieve, vendor: vendorMeRetrieve, store: storeMeRetrieve };
const moduleFetchers = {
  admin: adminModulesList,
  vendor: vendorModulesList,
  store: storeModulesList,
};

export function useSession() {
  return useQuery<AuthState>({
    queryKey: SESSION_KEY,
    queryFn: auth.getSession,
    staleTime: 60_000,
  });
}

export function usePanelMe<T extends PanelMe = PanelMe>(panel: Panel, enabled = true) {
  return useQuery<T, ApiError>({
    queryKey: meKey(panel),
    queryFn: async () => (await meFetchers[panel]()).data as T,
    enabled,
    staleTime: 60_000,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export function useInstalledModules(panel: Panel) {
  return useQuery<InstalledModule[], ApiError>({
    queryKey: modulesKey(panel),
    queryFn: async () => (await moduleFetchers[panel]()).data,
    staleTime: 5 * 60_000,
  });
}

export const LOGGED_OUT: AuthState = {
  authenticated: false,
  user: null,
  pendingFlow: null,
  flows: [],
};

/**
 * Logs out: marks the session as logged out (the protected area then redirects to login) and
 * drops every other cached query, so no data from the previous user survives.
 */
export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: auth.logout,
    onSettled: () => {
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY[0] });
      client.setQueryData(SESSION_KEY, LOGGED_OUT);
    },
  });
}

// ── Panel context: which panel, which modules, who is logged in ──────

export interface PanelContextValue {
  panel: Panel;
  product: string;
  modules: ModuleDefinition[];
  installed: Set<string>;
  me: PanelMe;
  /** Resolved feature-flag values for this panel (see features.tsx). */
  features: Record<string, boolean | string>;
}

export const PanelContext = createContext<PanelContextValue | null>(null);

export function usePanel(): PanelContextValue {
  const value = useContext(PanelContext);
  if (!value) throw new Error("usePanel() must be used inside a panel app.");
  return value;
}

/**
 * Permission checks: Django permission codes in the admin (superusers can do everything); in the vendor panel the
 * store team-role codes (`store.edit`, `team.manage`…) that come with `/vendor/me`.
 */
export function usePermissions() {
  const { me } = usePanel();
  const admin = "permissions" in me ? (me as AdminMe) : null;
  const granted = new Set(admin?.permissions ?? []);
  const superuser = Boolean(admin?.is_superuser);
  return {
    can: (permission?: string) => !permission || superuser || granted.has(permission),
    roles: admin?.roles ?? [],
    isSuperuser: superuser,
  };
}
